import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { LightAsync as SyntaxHighlighter } from 'react-syntax-highlighter'
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json'
import { codify } from '../'
import {
    Section, ExternalLink, IconMessage, Heading,
    InlineCode, Subheading, UnorderedList, BlockCodeWrapper
} from '../s'
import Paragraph from 'components/typography/paragraph'
import { Block as BlockCode } from 'components/code'
import { ListItem } from 'components/list'
import { InfoCircleFilled as Info } from 'components/feather'

const WebhookUserEvents = () => {
    useEffect(() => {
        SyntaxHighlighter.registerLanguage('json', json)
    }, [])

    return (
        <Section>
            <Heading>
                <FormattedMessage
                    id="integration_webhook_heading_profile_events"
                    defaultMessage="User events" />
            </Heading>
            <Paragraph>
                <FormattedMessage
                    id="integration_webhook_preamble_how_to_use_3"
                    defaultMessage="All request bodies also include a <code>users</code> array, which contains an object for each affected user. These objects will always contain:"
                    values={{ code: codify }} />
            </Paragraph>
            <UnorderedList>
                <ListItem>
                    <InlineCode>id</InlineCode>
                    <UnorderedList>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_1"
                                defaultMessage="Always present" />
                        </ListItem>
                        <ListItem>
                            <span>
                                <FormattedMessage
                                    id="integration_webhook_preamble_how_to_use_3_list_item_3"
                                    defaultMessage="A unique identifier for the user" />
                            </span>
                            <UnorderedList>
                                <ListItem>
                                    <FormattedMessage
                                        id="integration_webhook_preamble_how_to_use_3_list_item_4"
                                        defaultMessage="This value will remain unchanged over the user’s lifetime." />
                                </ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_5"
                                defaultMessage="Value is a standard 128-bit UUID (see <link>rfc4122</link>)"
                                values={{ link: chunks => (
                                    <ExternalLink href="https://datatracker.ietf.org/doc/html/rfc4122">
                                        {chunks}
                                    </ExternalLink>
                                ) }} />
                        </ListItem>
                    </UnorderedList>
                </ListItem>
                <ListItem>
                    <InlineCode>email</InlineCode>
                    <UnorderedList>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_1"
                                defaultMessage="Always present" />
                        </ListItem>
                        <ListItem>
                            <span>
                                <FormattedMessage
                                    id="integration_webhook_preamble_how_to_use_3_list_item_6"
                                    defaultMessage="A unique identifier at the point where the request is sent: no two users can have the same email" />
                            </span>
                            <UnorderedList>
                                <ListItem>
                                    <FormattedMessage
                                        id="integration_webhook_preamble_how_to_use_3_list_item_7"
                                        defaultMessage="Note that a user’s email can be changed by a user with the appropriate permission, so a user is not guaranteed to have the same value in later requests." />
                                </ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_8"
                                defaultMessage="Value is a string, validated to be an email address" />
                        </ListItem>
                    </UnorderedList>
                </ListItem>
                <ListItem>
                    <InlineCode>employmentId</InlineCode>
                    <UnorderedList>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_9"
                                defaultMessage="Present if set, absent if the user does not have one" />
                        </ListItem>
                        <ListItem>
                            <span>
                                <FormattedMessage
                                    id="integration_webhook_preamble_how_to_use_3_list_item_10"
                                    defaultMessage="A unique identifier at the point where the request is sent: no two users can have the same employment ID" />
                            </span>
                            <UnorderedList>
                                <ListItem>
                                    <FormattedMessage
                                        id="integration_webhook_preamble_how_to_use_3_list_item_11"
                                        defaultMessage="Note that a user’s employment ID can be changed, or removed, by a user with the appropriate permission, so a user is not guaranteed to have the same value in later requests." />
                                </ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_12"
                                defaultMessage="Value is a string, no restriction other than uniqueness within the organization" />
                        </ListItem>
                    </UnorderedList>
                </ListItem>
            </UnorderedList>

            <Subheading>
                <FormattedMessage
                    id="integration_webhook_subheading_create"
                    defaultMessage="Create" />
            </Subheading>
            <Paragraph>
                <FormattedMessage
                    id="integration_webhook_preamble_create"
                    defaultMessage="A user or users being created in your Huma account is sent as a <code>POST</code> request, with full user objects for the created users in the <code>users</code> array."
                    values={{ code: codify }} />
            </Paragraph>
            <IconMessage className="info">
                <Info size={20} />
                <Paragraph className="compact">
                    <FormattedMessage
                        id="integration_webhook_example"
                        defaultMessage="For example" />
                </Paragraph>
            </IconMessage>
            <BlockCodeWrapper>
                <BlockCode
                    language="json"
                    code={codeUsersCreate} />
            </BlockCodeWrapper>

            <Subheading>
                <FormattedMessage
                    id="integration_webhook_subheading_update"
                    defaultMessage="Update" />
            </Subheading>
            <Paragraph>
                <FormattedMessage
                    id="integration_webhook_preamble_how_to_use_update"
                    defaultMessage="A regular update is sent as a <code>PATCH</code> request, with the <code>users</code> object(s) containing only identifiers and the actually changed field(s)."
                    values={{ code: codify }} />
            </Paragraph>
            <IconMessage className="info">
                <Info size={20} />
                <Paragraph className="compact">
                    <FormattedMessage
                        id="integration_webhook_example_update"
                        defaultMessage="For example, an employee changing their bank details to use IBAN:" />
                </Paragraph>
            </IconMessage>
            <BlockCodeWrapper>
                <BlockCode
                    language="json"
                    code={codeUsersUpdate} />
            </BlockCodeWrapper>

            <Subheading>
                <FormattedMessage
                    id="integration_webhook_subheading_delete"
                    defaultMessage="Delete" />
            </Subheading>
            <Paragraph>
                <FormattedMessage
                    id="integration_webhook_preamble_delete"
                    defaultMessage="A user or users being deleted in your Huma account is sent as a <code>DELETE</code> request, with the <code>users</code> object(s) containing only identifiers."
                    values={{ code: codify }} />
            </Paragraph>
            <IconMessage className="info">
                <Info size={20} />
                <Paragraph className="compact">
                    <FormattedMessage
                        id="integration_webhook_example"
                        defaultMessage="For example" />
                </Paragraph>
            </IconMessage>
            <BlockCodeWrapper>
                <BlockCode
                    language="json"
                    code={codeUsersDelete} />
            </BlockCodeWrapper>


            <Subheading>
                <FormattedMessage
                    id="integration_webhook_subheading_export"
                    defaultMessage="Export" />
            </Subheading>
            <Paragraph>
                <FormattedMessage
                    id="integration_webhook_how_to_use_export"
                    defaultMessage="An export is sent as one or more <code>POST</code> request(s), each detailing the full state of all fields supported by the webhook for all users, including empty values for fields that have not been set. Each request details up to 50 profiles, so if your organization has more than 50 users, the export will be sent as multiple requests. E.g. an export from an organizations with 132 users would receive three requests, the first two with 50 user objects each, followed by a final request with 32 user objects. "
                    values={{ code: codify }} />
            </Paragraph>
            <IconMessage className="info">
                <Info size={20} />
                <Paragraph className="compact">
                    <FormattedMessage
                        id="integration_webhook_example"
                        defaultMessage="For example" />
                </Paragraph>
            </IconMessage>
            <BlockCodeWrapper>
                <BlockCode
                    language="json"
                    code={codeUsersExport} />
            </BlockCodeWrapper>
        </Section>
    )
}

const codeUsersCreate = `{
    "topic": "users-create",
    "users": [
        {
            "id": "09d41e51-0963-4763-933b-8c9df093d653",
            "givenName": "Ida",
            "familyName": "Fossdal",
            "email": "ida.fossdal@godmat.no",
            "phone": "+47 955 55 167",
            "address": {
                "line1": "Fossvegen 82",
                "line2": null,
                "postalCode": "1823",
                "city": "Bergen",
                "country": "NO"
            },
            "birthDate": "1993-07-29",
            "bankAccount": {
                "type": "national",
                "number": "12345678903",
                "country": ""
            },
            "gender": "Female",
            "employmentId": "1",
            "employmentStartDate": "2001-09-14",
            "employmentEndDate": null,
            "employmentType": "Permanent",
            "employmentPercentage": 100
        },
        {
            "id": "d96434d7-9506-4d5f-a969-7d1eea8bc3d6",
            "givenName": "Jesper",
            "familyName": "Blom",
            "email": "jesper.blom@godmat.no",
            "phone": "+46 7555512",
            "address": {
                "line1": null,
                "line2": null,
                "postalCode": "520 43",
                "city": "Stockholm",
                "country": "SE"
            },
            "birthDate": "1989-09-08",
            "bankAccount": null,
            "gender": "Male",
            "employmentId": "31",
            "employmentStartDate": "2019-01-01",
            "employmentEndDate": null,
            "employmentType": "Permanent",
            "employmentPercentage": 100
        }
    ]
}`

const codeUsersUpdate = `{
    "users": [
        {
            "id": "d96434d7-9506-4d5f-a969-7d1eea8bc3d6",
            "email": "jesper.blom@godmat.no",
            "employmentId": "31",
            "bankAccount": {
                "type": "international",
                "iban": "FR7630006000011234567890189",
                "bic": "AGRIFRPP"
            }
        }
    ],
    "topic": "users-update"
}`

const codeUsersExport = `{
    "topic": "users-export",
    "users": [
        {
            "id": "c2c8d497-352a-4c21-bf7c-aefbaec37717",
            "givenName": "Patrick",
            "familyName": "Remen",
            "email": "patrick.remen@godmat.no",
            "phone": "+46 7455564",
            "address": {
                "line1": null,
                "line2": null,
                "postalCode": "464 95",
                "city": "Stockholm",
                "country": "SE"
            },
            "birthDate": "1987-02-13",
            "bankAccount": null,
            "gender": "Male",
            "employmentId": "30",
            "employmentStartDate": "2012-03-13",
            "employmentEndDate": null,
            "employmentType": "Permanent",
            "employmentPercentage": 100
        },
        {
            "id": "12b87a11-7b5a-4c77-bef2-f46522d99e1c",
            "givenName": "Laila",
            "familyName": "Støa",
            "email": "laila.stoa@godmat.no",
            "phone": "+47 455 53 217",
            "address": {
                "line1": "Kleivbakken 21",
                "line2": null,
                "postalCode": "2412",
                "city": "Bergen",
                "country": "NO"
            },
            "birthDate": "1981-08-04",
            "bankAccount": {
                "type": "national",
                "number": "12345678902",
                "country": ""
            },
            "gender": "Female",
            "employmentId": "2",
            "employmentStartDate": "1999-05-18",
            "employmentEndDate": null,
            "employmentType": "Permanent",
            "employmentPercentage": 60
        },
        {
            "id": "d96434d7-9506-4d5f-a969-7d1eea8bc3d6",
            "givenName": "Jesper",
            "familyName": "Blom",
            "email": "jesper.blom@godmat.no",
            "phone": "+46 7555512",
            "address": {
                "line1": null,
                "line2": null,
                "postalCode": "520 43",
                "city": "Stockholm",
                "country": "SE"
            },
            "birthDate": "1989-09-08",
            "bankAccount": {
                "type": "international",
                "iban": "FR7630006000011234567890189",
                "bic": "AGRIFRPP"
            },
            "gender": "Male",
            "employmentId": "31",
            "employmentStartDate": "2019-01-01",
            "employmentEndDate": null,
            "employmentType": "Permanent",
            "employmentPercentage": 100
        }
    ]
}`

const codeUsersDelete = `{
    "topic": "users-delete",
    "users": [
        {
            "id": "d5430b67-479c-456a-b2c4-4053789d2959",
            "email": "ida.fossdal@godmat.no",
            "employmentId": "1"
        }
    ]
}`

export default WebhookUserEvents